/* eslint-disable lfds/no-raw-token-strings -- --lfds-skeleton-* are internal custom properties from #utils/skeleton.css, not design tokens */
import tokens from '@lansforsakringar/core-tokens'
import { css } from 'lit'

import { vars, scoped } from '#utils/styles'

const locals = scoped('navigation-list-row', [
	'background_color',
	'border_color',
	'color',
	'color_secondary'
] as const)

export default css`
	:host {
		display: block;
		width: 100%;
		container-type: inline-size;
	}

	.content {
		display: grid;
		flex-grow: 1;
		align-items: stretch;
		gap: 0 ${vars(tokens.spacing_lg)};
		grid-template-columns: minmax(0, 1fr) minmax(0, fit-content(60%));
		grid-template-rows: auto auto;
	}

	.label-start,
	.sub-label-start,
	.label-end,
	.sub-label-end {
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: flex-start;
		min-width: 0;
	}

	.label-start,
	.sub-label-start {
		grid-column: 1;
	}

	.label-end,
	.sub-label-end {
		grid-column: 2;
		align-items: flex-end;
		text-align: end;
	}

	.label-start {
		grid-row: 1;
	}

	.sub-label-start {
		grid-row: 2;
	}

	.label-end {
		grid-row: 1;
	}

	.sub-label-end {
		grid-row: 2;
	}

	:host([data-has-label-start='false']) .label-start,
	:host([data-has-sub-label-start='false']) .sub-label-start,
	:host([data-has-label-end='false']) .label-end,
	:host([data-has-sub-label-end='false']) .sub-label-end {
		display: none;
	}

	/* Center align the label if only one label is in the column. Suspended
	 * while loading — an empty sibling needs its own row-1/row-2 slot for its
	 * placeholder bar, which a spanning/centered label would overlap. */
	:host(
			:not(
					[loading]
				)[data-has-label-start='true'][data-has-sub-label-start='false']
		)
		.label-start,
	:host(
			:not(
					[loading]
				)[data-has-label-start='false'][data-has-sub-label-start='true']
		)
		.sub-label-start,
	:host(
			:not([loading])[data-has-label-end='true'][data-has-sub-label-end='false']
		)
		.label-end,
	:host(
			:not([loading])[data-has-label-end='false'][data-has-sub-label-end='true']
		)
		.sub-label-end {
		grid-row: 1 / 3;
		justify-content: center;
	}

	/* Vertical align to the start when both labels in a column is used. */
	:host([data-has-label-start='true'][data-has-sub-label-start='true'])
		.label-start,
	:host([data-has-label-start='true'][data-has-sub-label-start='true'])
		.sub-label-start,
	:host([data-has-label-end='true'][data-has-sub-label-end='true']) .label-end,
	:host([data-has-label-end='true'][data-has-sub-label-end='true'])
		.sub-label-end {
		justify-content: flex-start;
	}

	@container (max-width: 17em) {
		.content {
			display: flex;
			flex-direction: column;
			gap: 0;
		}

		.label-start,
		.sub-label-start,
		.label-end,
		.sub-label-end {
			width: 100%;
			justify-content: flex-start;
			align-items: flex-start;
			text-align: start;
			margin: 0;
		}

		:host(:is([data-has-label-start='true'], [data-has-sub-label-start='true']))
			.label-end {
			margin-top: ${vars(tokens.spacing_sm)};
		}

		:host([data-has-label-start='false'][data-has-sub-label-start='false'])
			.label-end {
			margin-top: 0;
		}
	}

	slot[name='label-start'] {
		font-size: ${vars(tokens.text_size_label_default)};
		font-weight: ${vars(tokens.font_weight_regular)};
		line-height: ${vars(tokens.line_height_label_default)};
		color: ${vars(locals.color)};
		overflow-wrap: break-word;
		word-break: break-word;
	}

	slot[name='sub-label-start'] {
		font-size: ${vars(tokens.text_size_label_sm)};
		font-weight: ${vars(tokens.font_weight_regular)};
		line-height: ${vars(tokens.line_height_label_sm)};
		color: ${vars(locals.color_secondary)};
		overflow-wrap: break-word;
		word-break: break-word;
	}

	slot[name='label-end'] {
		font-size: ${vars(tokens.text_size_label_default)};
		font-weight: ${vars(tokens.font_weight_semibold)};
		line-height: ${vars(tokens.line_height_label_default)};
		color: ${vars(locals.color)};
		overflow-wrap: break-word;
		word-break: break-word;
	}

	slot[name='sub-label-end'] {
		font-size: ${vars(tokens.text_size_label_sm)};
		font-weight: ${vars(tokens.font_weight_regular)};
		line-height: ${vars(tokens.line_height_label_sm)};
		color: ${vars(locals.color_secondary)};
		overflow-wrap: break-word;
		word-break: break-word;
	}

	/*
	 * Loading — mask real slotted content in place instead of rendering a
	 * fixed set of placeholder shapes. Only quadrants that are actually
	 * populated shimmer, and the shimmer box inherits the real content's
	 * width/height (set above by data-has-*), so 1-1/1-2/2-1/2-2 all work
	 * without a variant prop. See DSR-012 §3.3.
	 *
	 * Requires force-shimmer in addition to loading — by default, already
	 * -known content stays visible while loading (e.g. a value simply being
	 * refreshed shouldn't disappear behind a shimmer); force-shimmer opts
	 * into masking it anyway (e.g. a recalculation where hiding the stale
	 * value until the new one arrives is preferred).
	 */
	:host([loading][force-shimmer][data-has-label-start='true']) .label-start,
	:host([loading][force-shimmer][data-has-sub-label-start='true'])
		.sub-label-start,
	:host([loading][force-shimmer][data-has-label-end='true']) .label-end,
	:host([loading][force-shimmer][data-has-sub-label-end='true'])
		.sub-label-end {
		position: relative;
	}

	:host([loading][force-shimmer]) .label-start ::slotted(*),
	:host([loading][force-shimmer]) .sub-label-start ::slotted(*),
	:host([loading][force-shimmer]) .label-end ::slotted(*),
	:host([loading][force-shimmer]) .sub-label-end ::slotted(*) {
		visibility: hidden;
	}

	:host([loading][force-shimmer][data-has-label-start='true'])
		.label-start::after,
	:host([loading][force-shimmer][data-has-sub-label-start='true'])
		.sub-label-start::after,
	:host([loading][force-shimmer][data-has-label-end='true']) .label-end::after,
	:host([loading][force-shimmer][data-has-sub-label-end='true'])
		.sub-label-end::after {
		content: '';
		position: absolute;
		inset: 0;
		border-radius: var(--lfds-skeleton-border-radius);
		border: var(--lfds-skeleton-border);
		background: var(--lfds-skeleton-background);
		background-clip: padding-box;
		animation: var(--lfds-skeleton-animation);
	}

	/*
	 * Loading with no real content assigned yet — there's nothing to mask
	 * in place, so fall back to a placeholder line sized to a random
	 * default width (set inline per quadrant) instead of hiding entirely.
	 */
	:host([loading][data-has-label-start='false']) .label-start,
	:host([loading][data-has-sub-label-start='false']) .sub-label-start,
	:host([loading][data-has-label-end='false']) .label-end,
	:host([loading][data-has-sub-label-end='false']) .sub-label-end {
		display: flex;
		position: relative;
		min-height: 1em;
	}

	:host([loading][data-has-label-start='false']) .label-start::after,
	:host([loading][data-has-sub-label-start='false']) .sub-label-start::after,
	:host([loading][data-has-label-end='false']) .label-end::after,
	:host([loading][data-has-sub-label-end='false']) .sub-label-end::after {
		content: '';
		position: absolute;
		/* Inset top/bottom slightly — unlike the "mask real content" bars
		 * above (sized to match real text exactly), these are a guessed
		 * placeholder with no text metrics to match, so a bit of breathing
		 * room keeps adjacent label/sub-label placeholder bars from
		 * reading as one fused shape. */
		top: 1px;
		left: 0;
		height: calc(100% - 2px);
		width: var(--lfds-skeleton-width, 65%);
		border-radius: var(--lfds-skeleton-border-radius);
		border: var(--lfds-skeleton-border);
		background: var(--lfds-skeleton-background);
		background-clip: padding-box;
		animation: var(--lfds-skeleton-animation);
	}

	/* The end column reads right-to-left (text-align: end) — anchor the
	 * placeholder to the right edge too, instead of the left. */
	:host([loading][data-has-label-end='false']) .label-end::after,
	:host([loading][data-has-sub-label-end='false']) .sub-label-end::after {
		left: auto;
		right: 0;
	}
`
